import { useState, useEffect } from "react";
import { X, Check, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  GlobalIntent,
  ALL_INTENTS,
  intentLabels,
  intentDescriptions,
  intentIcons,
} from "@/hooks/useGlobalIntents";

interface GlobalIntentGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (intents: GlobalIntent[]) => Promise<void> | void;
  initialIntents?: GlobalIntent[];
  isUpdate?: boolean;
}

export const GlobalIntentGateModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialIntents = [],
  isUpdate = false,
}: GlobalIntentGateModalProps) => {
  const [selected, setSelected] = useState<GlobalIntent[]>(initialIntents);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync with initialIntents when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelected(initialIntents.length > 0 ? initialIntents : []);
    }
  }, [isOpen, initialIntents]);

  if (!isOpen) return null;

  const isAllSelected = selected.length === ALL_INTENTS.length;

  const toggleIntent = (intent: GlobalIntent) => {
    setSelected((prev) =>
      prev.includes(intent)
        ? prev.filter((i) => i !== intent)
        : [...prev, intent]
    );
  };

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelected([]);
    } else {
      setSelected([...ALL_INTENTS]);
    }
  };

  const handleConfirm = async () => {
    if (selected.length === 0) return;

    setIsSubmitting(true);
    try {
      await onConfirm(selected);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {isUpdate ? "Update Your Intent" : "Choose Your Intent"}
          </h2>
          {isUpdate && (
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <p className="text-sm text-muted-foreground mb-4">
            Select one or more intents. You'll only see users with matching intentions.
          </p>

          {/* Select All */}
          <button
            onClick={toggleSelectAll}
            className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
              isAllSelected
                ? "border-primary bg-primary/10"
                : "border-border hover:border-primary/50"
            }`}
          >
            {isAllSelected ? (
              <CheckSquare className="w-5 h-5 text-primary" />
            ) : (
              <Square className="w-5 h-5 text-muted-foreground" />
            )}
            <span className="font-medium">Select All</span>
          </button>

          <div className="h-px bg-border my-3" />

          {/* Intent Options */}
          {ALL_INTENTS.map((intent) => {
            const isSelected = selected.includes(intent);
            return (
              <button
                key={intent}
                onClick={() => toggleIntent(intent)}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{intentIcons[intent]}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium">{intentLabels[intent]}</p>
                      {isSelected && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {intentDescriptions[intent]}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <Button
            className="w-full"
            disabled={selected.length === 0 || isSubmitting}
            onClick={handleConfirm}
          >
            {isSubmitting
              ? "Saving..."
              : isUpdate
              ? "Update Intent"
              : "Continue"}
          </Button>
          {!isUpdate && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              You can change this anytime from Global Connect
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
